import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Camera, Wine, ShoppingCart, Heart, ArrowRight } from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Camera,
      title: 'Scan Your Bar',
      description: 'Simply snap a photo of your liquor collection and let AI identify your bottles.'
    },
    {
      icon: Wine,
      title: 'Get Recipes',
      description: 'Discover cocktails you can make right now with what you already have.'
    },
    {
      icon: ShoppingCart,
      title: 'Smart Shopping',
      description: 'Get personalized suggestions on what to buy to unlock more cocktails.'
    },
    {
      icon: Heart,
      title: 'Save Favorites',
      description: 'Build your personal cocktail collection and never lose a great recipe.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1681821675154-5f50ede43f6a?w=1920&q=80)'
          }}
        />
        <div className="hero-gradient" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6 animate-fadeIn">
              <span className="text-[#F5F5F5]">Your Bar,</span>
              <br />
              <span className="text-[#D4AF37]">Unlimited Cocktails</span>
            </h1>
            
            <p className="text-lg md:text-xl text-[#A3A3A3] mb-10 max-w-xl animate-fadeIn stagger-1">
              Scan your liquor collection and discover all the cocktails you can make. 
              Never wonder "what can I make?" again.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeIn stagger-2">
              <Button
                onClick={() => navigate('/register')}
                className="bg-[#D4AF37] text-black hover:bg-[#F4CF57] font-bold rounded-full px-8 py-6 text-lg gold-glow btn-press"
                data-testid="get-started-btn"
              >
                Get Started Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full px-8 py-6 text-lg btn-press"
                data-testid="login-btn"
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#F5F5F5] mb-4">
              How It Works
            </h2>
            <p className="text-[#A3A3A3] text-lg max-w-2xl mx-auto">
              From photo to cocktail in seconds. It's that simple.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="bg-[#121212] border border-white/5 rounded-2xl p-8 card-lift animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#F5F5F5] mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="glass rounded-3xl p-12 md:p-16">
            <Wine className="w-16 h-16 text-[#D4AF37] mx-auto mb-8" />
            <h2 className="text-3xl md:text-4xl font-bold text-[#F5F5F5] mb-4">
              Ready to Mix?
            </h2>
            <p className="text-[#A3A3A3] text-lg mb-8 max-w-xl mx-auto">
              Join thousands of home bartenders who discovered new cocktails in their own bars.
            </p>
            <Button
              onClick={() => navigate('/register')}
              className="bg-[#D4AF37] text-black hover:bg-[#F4CF57] font-bold rounded-full px-10 py-6 text-lg gold-glow btn-press"
              data-testid="cta-get-started-btn"
            >
              Start Scanning
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Wine className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-bold text-[#F5F5F5]">Mixed Up</span>
          </div>
          <p className="text-[#525252] text-sm">
            © 2024 Mixed Up. Drink responsibly.
          </p>
        </div>
      </footer>
    </div>
  );
}
